export default class Result<T> {
  private readonly value: T | Error

  private constructor(value: T | Error) {
    this.value = value
  }

  // region state checks
  isSuccess(): boolean {
    return !(this.value instanceof Error)
  }

  isFailure(): boolean {
    return this.value instanceof Error
  }
  // endregion

  // region getters
  getOrNull(): T | null {
    if (this.isFailure()) {
      return null
    }
    return this.value as T
  }

  errorOrNull(): Error | null {
    if (this.isFailure()) {
      return this.value as Error
    }
    return null
  }

  getOrElse(defaultValue: T | (() => T)): T {
    if (this.isSuccess()) {
      return this.value as T
    }
    return typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue
  }

  getOrThrow(message?: string): T {
    if (this.isFailure()) {
      const err = this.value as Error
      throw message ? new Error(`${message}: ${err.message}`) : err
    }
    return this.value as T
  }
  // endregion

  toString(): string {
    if (this.isFailure()) {
      const err = this.value as Error
      const msg = err && err.message ? err.message : String(err)
      return `Failure(${msg})`
    } else {
      return `Success(${String(this.value)})`
    }
  }

  // region constructors
  static success<T>(value: T): Result<T> {
    return new Result<T>(value)
  }

  /**
   * Creates a failure Result. Accepts unknown and normalizes to an Error instance.
   */
  static failure<T>(error: unknown): Result<T> {
    const normalized = error instanceof Error ? error : new Error(String(error))
    return new Result<T>(normalized as Error)
  }

  /**
   * Executes the provided function and captures any thrown error into a Result.
   */
  static try<T>(fn: () => T): Result<T> {
    try {
      return Result.success(fn())
    } catch (e) {
      return Result.failure<T>(e)
    }
  }
  // endregion

  // region transforms
  map<U>(transform: (value: T) => U): Result<U> {
    if (this.isFailure()) {
      return Result.failure<U>(this.value as Error)
    }
    try {
      return Result.success<U>(transform(this.value as T))
    } catch (e) {
      return Result.failure<U>(e)
    }
  }

  mapError(transform: (error: Error) => Error): Result<T> {
    if (this.isFailure()) {
      try {
        const newErr = transform(this.value as Error)
        return Result.failure<T>(newErr)
      } catch (e) {
        return Result.failure<T>(e)
      }
    }
    return this
  }
  // endregion

  fold<R>(onSuccess: (value: T) => R, onFailure: (error: Error) => R): R {
    if (this.isFailure()) {
      return onFailure(this.value as Error)
    }
    return onSuccess(this.value as T)
  }

  onFailure(action: (error: Error) => void): Result<T> {
    if (this.isFailure()) {
      const error = this.value as Error
      action(error)
    }
    return this
  }

  onSuccess(action: (value: T) => void): Result<T> {
    if (this.isSuccess()) {
      action(this.value as T)
    }
    return this
  }
}
